import React, { useState, useEffect } from 'react';
import MatchingEngine from '../../components/Matching/MatchingEngine';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Matching = () => {
  const [activeTab, setActiveTab] = useState('suggestions');

  const tabs = [
    { id: 'suggestions', name: 'Suggestions', icon: SparklesIcon },
    { id: 'preferences', name: 'Préférences', icon: AdjustmentsHorizontalIcon },
    { id: 'history', name: 'Historique', icon: ChartBarIcon }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'suggestions':
        return <MatchingEngine />;
      case 'preferences':
        return <MatchingPreferences />;
      case 'history':
        return <MatchingHistory />;
      default:
        return <MatchingEngine />;
    }
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">
            Matching Intelligent
          </h1>
          <p className="text-gray-600">
            Trouvez les meilleures correspondances avec des entrepreneurs et investisseurs
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Composant pour les préférences de matching
const MatchingPreferences = () => {
  const [preferences, setPreferences] = useState({
    categories: [],
    countries: [],
    minFunding: '',
    maxFunding: '',
    minROI: '',
    maxROI: '',
    teamSize: '',
    projectStage: []
  });
  const [saving, setSaving] = useState(false);

  // Charger les préférences sauvegardées
  useEffect(() => {
    axios.get('/api/matching/preferences')
      .then(res => {
        if (res.data?.data) setPreferences(res.data.data);
      })
      .catch(() => {});
  }, []);

  const categories = [
    'Technologie', 'Santé', 'Finance', 'Éducation', 'E-commerce', 
    'Immobilier', 'Transport', 'Énergie', 'Agriculture', 'Tourisme'
  ];

  const countries = [
    // Afrique de l'Ouest
    'Sénégal', "Côte d'Ivoire", 'Mali', 'Burkina Faso', 'Niger',
    'Guinée', 'Bénin', 'Togo', 'Nigeria', 'Ghana',
    'Sierra Leone', 'Libéria', 'Gambie', 'Guinée-Bissau', 'Cap-Vert', 'Mauritanie',
    // Afrique Centrale
    'Cameroun', 'Congo-Brazzaville', 'République Démocratique du Congo',
    'Gabon', 'Tchad', 'République centrafricaine', 'Guinée équatoriale',
    // Afrique de l'Est
    'Kenya', 'Éthiopie', 'Tanzanie', 'Ouganda', 'Rwanda', 'Burundi',
    'Djibouti', 'Comores', 'Madagascar', 'Maurice', 'Seychelles',
    // Afrique du Nord
    'Maroc', 'Algérie', 'Tunisie', 'Égypte', 'Libye',
    // Afrique Australe
    'Afrique du Sud', 'Angola', 'Mozambique', 'Zambie', 'Zimbabwe',
    // Europe francophone
    'France', 'Belgique', 'Suisse', 'Luxembourg',
    // Autres
    'Canada', 'Haïti',
  ];

  const stages = [
    'Idée', 'Prototype', 'MVP', 'Croissance', 'Expansion'
  ];

  const handleCategoryToggle = (category) => {
    setPreferences(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleCountryToggle = (country) => {
    setPreferences(prev => ({
      ...prev,
      countries: prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country]
    }));
  };

  const handleStageToggle = (stage) => {
    setPreferences(prev => ({
      ...prev,
      projectStage: prev.projectStage.includes(stage)
        ? prev.projectStage.filter(s => s !== stage)
        : [...prev.projectStage, stage]
    }));
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      await axios.post('/api/matching/preferences', preferences);
      toast.success('Préférences sauvegardées avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde des préférences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-text mb-6">
          Préférences de matching
        </h2>

        {/* Catégories */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-text mb-3">Catégories d'intérêt</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleCategoryToggle(category)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  preferences.categories.includes(category)
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Pays */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-text mb-3">Pays d'intérêt</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {countries.map(country => (
              <button
                key={country}
                onClick={() => handleCountryToggle(country)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  preferences.countries.includes(country)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {country}
              </button>
            ))}
          </div>
        </div>

        {/* Financement */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-text mb-3">Gamme de financement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant minimum (€)
              </label>
              <input
                type="number"
                value={preferences.minFunding}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  minFunding: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant maximum (€)
              </label>
              <input
                type="number"
                value={preferences.maxFunding}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  maxFunding: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="1000000"
              />
            </div>
          </div>
        </div>

        {/* ROI */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-text mb-3">ROI attendu (%)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ROI minimum (%)
              </label>
              <input
                type="number"
                value={preferences.minROI}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  minROI: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ROI maximum (%)
              </label>
              <input
                type="number"
                value={preferences.maxROI}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  maxROI: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="50"
              />
            </div>
          </div>
        </div>

        {/* Stade du projet */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-text mb-3">Stade du projet</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {stages.map(stage => (
              <button
                key={stage}
                onClick={() => handleStageToggle(stage)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  preferences.projectStage.includes(stage)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {stage}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleSavePreferences}
          disabled={saving}
          className="w-full bg-primary-500 text-white py-3 px-4 rounded-lg hover:bg-primary-600 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? 'Sauvegarde en cours...' : 'Sauvegarder les préférences'}
        </button>
      </div>
    </div>
  );
};

// Composant pour l'historique de matching
const MatchingHistory = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-text mb-6">
        Historique de matching
      </h2>
      <div className="text-center py-8">
        <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Aucun historique de matching disponible</p>
        <p className="text-sm text-gray-500 mt-1">
          Vos interactions avec les suggestions de matching apparaîtront ici
        </p>
      </div>
    </div>
  );
};

export default Matching;
